import { io } from "socket.io-client";

const listeners = {
  message: new Set(),
  conversationUpdate: new Set(),
  error: new Set(),
  connect: new Set(),
  disconnect: new Set(),
};

let socket = null;

function safeText(value = "") {
  return String(value || "").trim();
}

function getEnv(name) {
  if (typeof process !== "undefined" && process.env?.[name]) {
    return safeText(process.env[name]);
  }

  return "";
}

function stripApiSuffix(url = "") {
  const safeUrl = safeText(url).replace(/\/+$/, "");

  if (!safeUrl || safeUrl === "/api") return "";

  return safeUrl.replace(/\/api$/i, "");
}

export function resolveChatSocketUrl() {
  const explicitSocketUrl = stripApiSuffix(getEnv("REACT_APP_SOCKET_URL"));

  if (explicitSocketUrl) return explicitSocketUrl;

  return stripApiSuffix(getEnv("REACT_APP_API_URL"));
}

function notify(type, payload) {
  listeners[type].forEach((handler) => {
    try {
      handler(payload);
    } catch (error) {
      if (type !== "error") {
        notify("error", error);
      }
    }
  });
}

function createSocket() {
  const url = resolveChatSocketUrl();
  const options = {
    withCredentials: true,
    transports: ["websocket", "polling"],
  };
  const nextSocket = url ? io(url, options) : io(options);

  nextSocket.on("connect", () => notify("connect", nextSocket));
  nextSocket.on("disconnect", (reason) => notify("disconnect", reason));
  nextSocket.on("connect_error", (error) => notify("error", error));
  nextSocket.on("chat:error", (payload) => notify("error", payload));
  nextSocket.on("chat:message", (payload) => notify("message", payload));
  nextSocket.on("chat:conversation:update", (payload) =>
    notify("conversationUpdate", payload),
  );

  return nextSocket;
}

export function getChatSocket() {
  if (!socket) {
    socket = createSocket();
  }

  if (!socket.connected && socket.disconnected) {
    socket.connect();
  }

  return socket;
}

function addListener(type, handler) {
  if (typeof handler !== "function") return () => {};

  listeners[type].add(handler);

  return () => listeners[type].delete(handler);
}

export function subscribeChatSocket({
  onMessage,
  onConversationUpdate,
  onError,
  onConnect,
  onDisconnect,
} = {}) {
  getChatSocket();

  const cleanups = [
    addListener("message", onMessage),
    addListener("conversationUpdate", onConversationUpdate),
    addListener("error", onError),
    addListener("connect", onConnect),
    addListener("disconnect", onDisconnect),
  ];

  return () => cleanups.forEach((cleanup) => cleanup());
}

export function joinChatConversation(conversationId = "") {
  const safeConversationId = safeText(conversationId);

  if (!safeConversationId) {
    return Promise.resolve({ ok: false, error: "Missing conversation id." });
  }

  const activeSocket = getChatSocket();

  return new Promise((resolve) => {
    activeSocket.timeout(5000).emit(
      "chat:join",
      { conversationId: safeConversationId },
      (error, response) => {
        if (error) {
          resolve({
            ok: false,
            error,
          });
          return;
        }

        resolve(response);
      },
    );
  });
}

export function sendChatMessageOverSocket({
  conversationId = "",
  message = "",
  clientMessageId = "",
} = {}) {
  const activeSocket = getChatSocket();

  return new Promise((resolve) => {
    activeSocket.timeout(5000).emit(
      "chat:send",
      {
        conversationId: safeText(conversationId),
        message: safeText(message),
        clientMessageId: safeText(clientMessageId),
      },
      (error, response) => {
        if (error) {
          resolve({
            ok: false,
            error,
            clientMessageId: safeText(clientMessageId),
          });
          return;
        }

        resolve(response);
      },
    );
  });
}

export function resetChatSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

const chatSocket = {
  getChatSocket,
  joinChatConversation,
  resetChatSocket,
  resolveChatSocketUrl,
  sendChatMessageOverSocket,
  subscribeChatSocket,
};

export default chatSocket;
